import { Link } from 'react-router-dom';
import { Mail, Facebook } from 'lucide-react';
import TikTokIcon from './icons/TikTokIcon';

const LOGO_URL = "/api/files/jsfragrance/products/be3c5fd6-5c5e-4e28-9eca-fed1e5e06eea.png";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#0A0A0A] border-t border-white/10 py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12">
          {/* Brand */}
          <div>
            <img src={LOGO_URL} alt="JS Fragrance" className="h-16 w-auto mb-4" />
            <p className="text-white/50 text-sm leading-relaxed">
              Our Quality. Our Distinction.<br />
              Luxury should be experienced, not overpriced.
            </p>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-[#1C7D3F] mb-4 font-sans font-medium">Company</h4>
            <div className="flex flex-col gap-2">
              <Link to="/about" className="text-white/50 hover:text-white text-sm transition-colors">About Us</Link>
              <Link to="/about#contact" className="text-white/50 hover:text-white text-sm transition-colors">Contact</Link>
              <Link to="/ambassador-apply" className="text-[#D4AF37] hover:text-[#F3E5AB] text-sm transition-colors font-medium">Become an Ambassador</Link>
              <Link to="/wholesale" className="text-[#D4AF37] hover:text-[#F3E5AB] text-sm transition-colors font-medium">Wholesale &amp; Distributor</Link>
              <Link to="/blog" className="text-white/50 hover:text-white text-sm transition-colors">Blog</Link>
              <Link to="/faq" className="text-white/50 hover:text-white text-sm transition-colors">FAQ</Link>
            </div>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-[#1C7D3F] mb-4 font-sans font-medium">Legal</h4>
            <div className="flex flex-col gap-2">
              <Link to="/terms" className="text-white/50 hover:text-white text-sm transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="text-white/50 hover:text-white text-sm transition-colors">Privacy Policy</Link>
              <Link to="/refund-policy" className="text-white/50 hover:text-white text-sm transition-colors">Refund Policy</Link>
              <Link to="/shipping-policy" className="text-white/50 hover:text-white text-sm transition-colors">Shipping Policy</Link>
            </div>
          </div>

          {/* Contact Us */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-[#1C7D3F] mb-4 font-sans font-medium">Contact Us</h4>
            <div className="flex flex-col gap-3">
              <a
                href="https://www.facebook.com/profile.php?id=61588041885547"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-white/50 hover:text-[#1877F2] text-sm transition-colors group"
                data-testid="footer-facebook-link"
              >
                <Facebook className="w-4.5 h-4.5 group-hover:text-[#1877F2] transition-colors" />
                <span>JS Fragrance</span>
              </a>
              <a
                href="https://www.tiktok.com/@jsfragranceofficial"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-white/50 hover:text-white text-sm transition-colors group"
                data-testid="footer-tiktok-link"
              >
                <TikTokIcon className="w-4 h-4 group-hover:text-white transition-colors" />
                <span>@jsfragranceofficial</span>
              </a>
              <a
                href="mailto:support@jsfragrances.com"
                className="flex items-center gap-2.5 text-white/50 hover:text-[#D4AF37] text-sm transition-colors group"
                data-testid="footer-email-link"
              >
                <Mail className="w-4.5 h-4.5 group-hover:text-[#D4AF37] transition-colors" />
                <span>support@jsfragrances.com</span>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-10 pt-8">
          {/* #23 — Trust badges + payment methods */}
          <div className="flex flex-col gap-5 pb-6 border-b border-white/5" data-testid="footer-trust-row">
            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
              <div className="flex items-center gap-2.5">
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-sans">Secure checkout</span>
                <span className="inline-flex items-center h-6 px-2 bg-white/5 border border-white/10 text-[10px] font-mono text-white/70 rounded-sm tracking-wider">SSL</span>
                <span className="inline-flex items-center h-6 px-2 bg-white/5 border border-white/10 text-[10px] font-mono text-white/70 rounded-sm tracking-wider">256-BIT</span>
                <span className="inline-flex items-center h-6 px-2 bg-[#1C7D3F]/10 border border-[#1C7D3F]/30 text-[10px] font-mono text-[#1C7D3F] rounded-sm tracking-wider">VERIFIED PH</span>
              </div>

              <div className="flex items-center gap-2 flex-wrap" data-testid="footer-payments">
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-sans mr-1">We accept</span>
                {[
                  { label: 'GCash', bg: '#007DFE', fg: '#fff' },
                  { label: 'Maya',  bg: '#08C176', fg: '#fff' },
                  { label: 'BPI',   bg: '#A21A2D', fg: '#fff' },
                  { label: 'VISA',  bg: '#1A1F71', fg: '#fff' },
                  { label: 'MC',    bg: '#EB001B', fg: '#fff' },
                  { label: 'COD',   bg: '#D4AF37', fg: '#000' },
                ].map(p => (
                  <span
                    key={p.label}
                    data-testid={`pay-${p.label.toLowerCase()}`}
                    className="inline-flex items-center justify-center h-6 px-2.5 text-[10px] font-sans font-bold tracking-wider rounded-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]"
                    style={{ background: p.bg, color: p.fg, lineHeight: 1, minWidth: '38px' }}
                  >
                    {p.label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center space-y-2 pt-6">
          <p className="text-white/20 text-[10px] font-sans leading-relaxed max-w-md mx-auto">
            JS Fragrance is not affiliated with or endorsed by any original brand owner. All product names and trademarks are the property of their respective owners.
          </p>
          <p className="text-white/30 text-xs tracking-widest uppercase" data-testid="footer-copyright">
            &copy; {new Date().getFullYear()} JS Fragrance. All rights reserved.
          </p>
          <p className="text-white/15 text-[10px] font-mono" data-testid="footer-version">v2.5.1</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
